import { Link } from "react-router-dom";
import { PlayCircle, Clock, Loader2, Lock } from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import LiveShareBar from "@/components/landing/LiveShareBar";
import ReplayChapters from "@/components/ReplayChapters";
import { useStreamPlayer } from "@/hooks/useStreamPlayer";

const fmtDuration = (s) => {
  if (!s) return null;
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  return h ? `${h}h ${m.toString().padStart(2, "0")}m` : `${m} min`;
};
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" }) : "");

function Gated({ thumbnail, title }) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-border bg-black shadow-lg" data-testid="landing-replay-gated">
      <AspectRatio ratio={16 / 9}>
        {thumbnail && <img src={thumbnail} alt="" className="h-full w-full object-cover opacity-50" />}
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center text-white">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/15 backdrop-blur"><Lock className="h-5 w-5" /></span>
          <p className="font-display text-lg font-semibold">Members-only replay</p>
          <p className="max-w-sm text-sm text-white/80">Sign in to watch {title}. Not a member yet? Ask to join — it takes a minute.</p>
          <div className="flex flex-wrap justify-center gap-2 pt-1">
            <Link to="/login?next=/%23replay" className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="landing-replay-signin">Sign in to watch</Link>
            <Link to="/register" className="rounded-full border border-white/40 px-4 py-2 text-sm font-semibold text-white hover:bg-white/10" data-testid="landing-replay-join">Ask to join</Link>
          </div>
        </div>
      </AspectRatio>
    </div>
  );
}

function Player({ iframe, title, chapters }) {
  const { iframeRef, seek, currentTime } = useStreamPlayer(iframe);
  return (
    <>
      <div className="overflow-hidden rounded-2xl border border-border bg-black shadow-lg">
        <AspectRatio ratio={16 / 9}>
          <iframe ref={iframeRef} src={iframe} title={`${title} — replay`} className="h-full w-full" allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;" allowFullScreen data-testid="landing-replay-player" />
        </AspectRatio>
      </div>
      {chapters?.length > 0 && <div className="mt-3"><ReplayChapters chapters={chapters} currentTime={currentTime} onSeek={seek} testPrefix="landing-replay-chapter" /></div>}
    </>
  );
}

/** After a show ends, the homepage swaps the live player for this replay card (for a week). */
export default function ReplaySection({ replay }) {
  if (!replay) return null;
  const { session, ready, can_watch, iframe, thumbnail, duration, ended_at, chapters } = replay;
  return (
    <section id="replay" className="scroll-mt-24 border-b border-border bg-card" data-testid="landing-replay-section">
      <div className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-2 rounded-full bg-foreground px-3 py-1 text-xs font-semibold uppercase tracking-wide text-background" data-testid="landing-replay-badge">
            <PlayCircle className="h-3.5 w-3.5" /> Watch the replay
          </span>
          <h2 className="font-display text-xl font-semibold text-foreground sm:text-2xl" data-testid="landing-replay-title">{session.title}</h2>
          <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground sm:ml-auto" data-testid="landing-replay-meta">
            <Clock className="h-4 w-4" />Recorded {fmtWhen(ended_at)}{duration ? ` · ${fmtDuration(duration)}` : ""}
          </span>
        </div>
        {ready && can_watch && iframe ? (
          <Player iframe={iframe} title={session.title} chapters={chapters} />
        ) : ready ? (
          <>
            <Gated thumbnail={thumbnail} title={session.title} />
            {chapters?.length > 0 && <div className="mt-3"><ReplayChapters chapters={chapters} disabled testPrefix="landing-replay-chapter" /></div>}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-border bg-secondary/40 px-6 py-14 text-center" data-testid="landing-replay-processing">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <p className="font-medium text-foreground">The replay is being processed</p>
            <p className="max-w-md text-sm text-muted-foreground">We’re finishing the recording of today’s conversation. It usually appears here within a few minutes of the show ending — this page updates on its own.</p>
          </div>
        )}
        <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-muted-foreground">
            Missed it live? Catch the whole conversation here. <Link to={`/portal/hub/${session.session_id}`} className="font-medium text-primary hover:underline" data-testid="landing-replay-hub">Members can revisit the questions and chat</Link> from the show.
          </p>
          {ready && <LiveShareBar title={session.title} mode="replay" testPrefix="landing-replay-share" />}
        </div>
      </div>
    </section>
  );
}
